import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import LoginPage from './components/Login';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import { normalizeRole } from './finance/auth/permissions';
import HrDashboard from './components/HrDashboard';
import EmpDashboard from './components/Employee/EmpDashboard';
import CalendarPage from './components/Calendar/CalendarPage';
import MeetingDetailsPage from './components/Calendar/MeetingDetailsPage';
import EmpLeavesAndWfh from './components/Employee/EmpLeavesAndWfh';
import ProductManagement from './components/Products/ProductManagement';
import ProductDetail from './components/Products/ProductDetail';
import ProjectDashboard from './components/Projects/ProjectDashboard';
import ProjectDetails from './components/Projects/ProjectDetails';
import TasksView from './components/Tasks/TasksView';
import TaskDetails from './components/Tasks/TaskDetails';
import TaskEditorPage from './components/Tasks/TaskEditorPage';
import EmployeeProfile from './components/Employee/EmployeeProfile';
import EmployeeFinancePage from './components/Employee/EmployeeFinancePage';
import MyPayslips from './components/Employee/MyPayslips';
import HrTimeOffApprovals from './components/Hr/HrTimeOffApprovals';
import HrLeaveManagementPage from './components/Hr/HrLeaveManagementPage';
import HrLeaveDetailPage from './components/Hr/HrLeaveDetailPage';
import HrAllLeaves from './components/HrAllLeaves';
import HrTeams from './components/HrTeams';
import ProfilePage from './components/ProfilePage';
import CertificateUpload from './components/Employee/CertificateUpload';
import ViewEmployeeProfile from './components/ViewEmployeeProfile';
import HrProjectManagement from './components/HrProjectManagement';
import HrAttendance from './components/HrAttendance';
import HrProductDetail from './components/HrProductDetail';
import HrTasksView from './components/HrTasksView';
import TeamPresence from './components/Hr/TeamPresence';
import ManagerLeaveApprovals from './components/Manager/ManagerLeaveApprovals';
import EmployeeLeaveSummaryPage from './components/Manager/EmployeeLeaveSummaryPage';
import EmpLeaveBreakdownPage from './components/Employee/EmpLeaveBreakdownPage';


import NotificationsPage from './components/NotificationsPage';
import TechDashboard from './components/IT/TechDashboard';
import DocumentationPortal from './components/Docs/DocumentationPortal';
import MainLayout from './components/Layout/MainLayout';
import OnboardingPage from './components/OnboardingPage';
import AdminOnboardingDocsPage from './finance/pages/admin/AdminOnboardingDocsPage';
import HiringDashboard from './components/Hr/HiringDashboard';
import SalesDashboard from './components/sales/SalesDashboard';
import SalesLeads from './components/sales/SalesLeads';
import SalesTutorials from './components/sales/SalesTutorials';
import LeadDetails from './components/sales/LeadDetails';

import MySpaceTasks from './components/MySpace/MySpaceTasks';
import MySpaceTaskDetails from './components/MySpace/MySpaceTaskDetails';

import { AuthProvider, useAuth } from './finance/auth/AuthContext';
import FinanceRoutes from './finance/routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const HrProtectedRoute = () => {
  const { isAuthenticated, role, depts, isSuperadmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const isAdmin = String(role).toUpperCase() === 'ADMIN' || isSuperadmin;
  const isHr = depts?.some(d => String(d).toUpperCase() === 'HR') || isSuperadmin;
  if (!isAdmin || !isHr) {
    return <Navigate to="/leaves-wfh" replace />;
  }
  return <Outlet />;
};

const SalesProtectedRoute = () => {
  const { isAuthenticated, depts, isSuperadmin, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const isAdmin = String(role).toUpperCase() === 'ADMIN' || isSuperadmin;
  const hasSalesDept = depts?.some((d) => String(d).toUpperCase() === 'SALES') || isAdmin;
  if (!hasSalesDept) {
    return <Navigate to="/leaves-wfh" replace />;
  }
  return <Outlet />;
};

const SuperAdminProtectedRoute = () => {
  const { isAuthenticated, isSuperadmin, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const normRole = normalizeRole(role);
  const isSuper = isSuperadmin || normRole === 'SUPERADMIN';
  if (!isSuper) {
    return <Navigate to="/leaves-wfh" replace />;
  }
  return <Outlet />;
};

const RoleBasedRedirect = () => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return <Navigate to="/my-space/tasks" replace />;
};

function AppShell() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<RoleBasedRedirect />} />

      {/* Routes wrapped in MainLayout */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>

        {/* Protected HR Admin Routes */}
        <Route element={<HrProtectedRoute />}>
          <Route path="/hr/leaves" element={<HrLeaveManagementPage />} />
          <Route path="/hr/leave-management" element={<HrLeaveManagementPage />} />
          <Route path="/hr/employee-leave/:employeeId" element={<EmployeeLeaveSummaryPage />} />
          <Route path="/hr/leave-approvals" element={<HrTimeOffApprovals />} />
          <Route path="/hr/all-leaves" element={<HrAllLeaves />} />
          <Route path="/hr/teams" element={<HrTeams />} />
          <Route path="/hr/employee/:id" element={<ViewEmployeeProfile />} />
          <Route path="/hr/attendance" element={<HrAttendance />} />

          <Route path="/hr/team-presence" element={<TeamPresence />} />
        </Route>

        <Route path="/EmpDashboard" element={<Navigate to="/leaves-wfh" replace />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/my-space/tasks" element={<MySpaceTasks />} />
        <Route path="/my-space/tasks/:taskId" element={<MySpaceTaskDetails />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/calendar/meeting/:id" element={<MeetingDetailsPage />} />
        <Route path="/EmpLeaveManagement" element={<EmpLeavesAndWfh />} />
        <Route path="/leaves-wfh" element={<EmpLeavesAndWfh />} />
        <Route path="/leave-breakdown" element={<EmpLeaveBreakdownPage />} />
        <Route path="/employee/leave-breakdown" element={<EmpLeaveBreakdownPage />} />
        <Route path="/leaves-wfh/:id" element={<HrLeaveDetailPage />} />
        <Route path="/hr/leave/:id" element={<HrLeaveDetailPage />} />
        <Route path="/manager/leave/:id" element={<HrLeaveDetailPage />} />
        <Route path="/manager/leave-approvals" element={<ManagerLeaveApprovals />} />
        <Route path="/manager/employee-leave/:employeeId" element={<EmployeeLeaveSummaryPage />} />
        <Route path="/TasksView" element={<TasksView />} />
        <Route path="/ProjectDashboard" element={<ProjectDashboard />} />
        <Route path="/EmpProducts" element={<ProductManagement />} />
        <Route path="/EmpProducts/:productId" element={<ProductDetail />} />
        <Route path="/ProjectDetails/:projectId" element={<ProjectDetails />} />
        <Route path="/ProjectDetails/:projectId/task/new" element={<TaskEditorPage />} />
        <Route path="/ProjectDetails/:projectId/task/:taskId" element={<TaskDetails />} />
        <Route path="/ProjectDetails/:projectId/task/:taskId/edit" element={<TaskEditorPage />} />
        <Route path="/EmployeeProfile" element={<EmployeeProfile />} />
        <Route path="/my-finance" element={<EmployeeFinancePage />} />
        <Route path="/finance/payroll/my" element={<MyPayslips />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/certificate" element={<CertificateUpload />} />
        <Route path="/notifications" element={<NotificationsPage />} />

        <Route element={<SalesProtectedRoute />}>
          <Route path="/sales/dashboard" element={<SalesDashboard />} />
          <Route path="/sales/leads" element={<SalesLeads />} />
          <Route path="/sales/leads/:id" element={<LeadDetails />} />
          <Route path="/sales/tutorials" element={<SalesTutorials />} />
        </Route>

        {/* Tech Routes */}
        <Route path="/it/dashboard" element={<TechDashboard />} />
        <Route path="/tech/docs" element={<DocumentationPortal />} />

        {/* Administration routes — limited to Superadmin only */}
        <Route element={<SuperAdminProtectedRoute />}>
          <Route path="/admin" element={<Navigate to="/admin/leaves" replace />} />
          <Route path="/admin/leave-manager" element={<Navigate to="/admin/leaves" replace />} />
          <Route path="/admin/leaves" element={<HrLeaveManagementPage />} />
          <Route path="/admin/leave-management" element={<HrLeaveManagementPage />} />
          <Route path="/admin/onboarding-docs" element={<AdminOnboardingDocsPage />} />
          <Route path="/admin/onboarding-docs/*" element={<AdminOnboardingDocsPage />} />
          <Route path="/admin/*" element={<FinanceRoutes />} />
        </Route>
        <Route path="/finance/*" element={<FinanceRoutes />} />
      </Route>
      <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
          <div className="App">
            <AppShell />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;

